// ============================================
// Station Status - Overall station availability
// ============================================
export enum StationStatus {
  ACTIVE = 'ACTIVE',
  IN_USE = 'IN_USE',
  INACTIVE = 'INACTIVE',
}

// ============================================
// Connector Status - OCPP connector states
// ============================================
export enum ConnectorStatus {
  AVAILABLE = 'Available',
  PREPARING = 'Preparing',
  CHARGING = 'Charging',
  SUSPENDED_EV = 'SuspendedEV',
  SUSPENDED_EVSE = 'SuspendedEVSE',
  FINISHING = 'Finishing',
  RESERVED = 'Reserved',
  UNAVAILABLE = 'Unavailable',
  FAULTED = 'Faulted',
  OCCUPIED = 'Occupied',
}

// ============================================
// Session/Charging Status - Charging session states
// ============================================
export enum SessionStatus {
  CHARGING = 'charging',
  PAUSED = 'paused',
  CLOSED = 'closed',
  FINISHED = 'finished',
  PREPARE = 'prepare',
  PENDING = 'pending',
  CONFIRMED = 'confirmed',
  CANCELLED = 'cancelled',
  // A charge the charger started with no driver's order behind it; the
  // backend records it, stops it and bills nobody.
  UNAUTHORIZED = 'unauthorized',
}

// ============================================
// Transaction Status - Payment transaction states
// ============================================
export enum TransactionStatus {
  SUCCESS = 'success',
  CREATED = 'created',
  FAILURE = 'failure',
  PROCESSING = 'processing',
  REVERSED = 'reversed',
  PENDING = 'pending',
}

// ============================================
// User Status - User account states
// ============================================
export enum UserStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  BLOCKED = 'blocked',
}

// ============================================
// User Role - User permission levels
// ============================================
export enum UserRole {
  ADMIN = 'admin',
  USER = 'user',
  MANAGER = 'manager',
}

// ============================================
// Badge Style Type - Maps to CSS classes for UI
// ============================================
export enum BadgeStyle {
  ACTIVE = 'active',
  IN_USE = 'in-use',
  INACTIVE = 'inactive',
  FAILURE = 'failure',
  REVERSED = 'reversed',
  CHARGING = 'charging',
  AVAILABLE = 'available',
  UNAVAILABLE = 'unavailable',
  PENDING = 'pending',
}

// ============================================
// Helper functions for status display
// ============================================

/**
 * Get display label for station status
 */
export const getStationStatusLabel = (status: StationStatus): string => {
  switch (status) {
    case StationStatus.ACTIVE:
      return 'Available';
    case StationStatus.IN_USE:
      return 'In Use';
    case StationStatus.INACTIVE:
      return 'Offline';
    default:
      return String(status);
  }
};

/**
 * Get CSS class for station status badge
 */
export const getStationStatusClass = (status: StationStatus): string => {
  switch (status) {
    case StationStatus.ACTIVE:
      return 'status-available';
    case StationStatus.IN_USE:
      return 'status-in-use';
    case StationStatus.INACTIVE:
      return 'status-offline';
    default:
      return '';
  }
};

/**
 * Get display label for connector status
 */
export const getConnectorStatusLabel = (status: ConnectorStatus): string => {
  switch (status) {
    case ConnectorStatus.AVAILABLE:
      return 'Available';
    case ConnectorStatus.PREPARING:
      return 'Preparing';
    case ConnectorStatus.CHARGING:
      return 'Charging';
    case ConnectorStatus.SUSPENDED_EV:
      return 'Suspended (EV)';
    case ConnectorStatus.SUSPENDED_EVSE:
      return 'Suspended (EVSE)';
    case ConnectorStatus.FINISHING:
      return 'Finishing';
    case ConnectorStatus.RESERVED:
      return 'Reserved';
    case ConnectorStatus.UNAVAILABLE:
      return 'Unavailable';
    case ConnectorStatus.FAULTED:
      return 'Faulted';
    case ConnectorStatus.OCCUPIED:
      return 'In Use';
    default:
      return String(status);
  }
};

export type ConnectorStatusGroup = 'available' | 'in-use' | 'offline' | 'inactive';

/**
 * Canonical status → group mapping. The SINGLE source of truth for which
 * connector states count as available / in-use / offline — every summary
 * count and badge must go through this so different tabs can't disagree.
 */
export const getConnectorStatusGroup = (
  status: ConnectorStatus,
): ConnectorStatusGroup | null => {
  switch (status) {
    case ConnectorStatus.AVAILABLE:
      return 'available';
    case ConnectorStatus.CHARGING:
    case ConnectorStatus.PREPARING:
    case ConnectorStatus.FINISHING:
    case ConnectorStatus.OCCUPIED:
    case ConnectorStatus.RESERVED:
      return 'in-use';
    case ConnectorStatus.UNAVAILABLE:
    case ConnectorStatus.FAULTED:
      return 'offline';
    case ConnectorStatus.SUSPENDED_EV:
    case ConnectorStatus.SUSPENDED_EVSE:
      return 'inactive';
    default:
      return null;
  }
};

/**
 * Get CSS class for connector status
 */
export const getConnectorStatusClass = (status: ConnectorStatus): string =>
  getConnectorStatusGroup(status) ?? '';

/**
 * Connector states where stopping a transaction makes sense.
 */
const BUSY_CONNECTOR_STATUSES: ReadonlySet<ConnectorStatus> = new Set([
  ConnectorStatus.OCCUPIED,
  ConnectorStatus.CHARGING,
  ConnectorStatus.PREPARING,
  ConnectorStatus.FINISHING,
  ConnectorStatus.SUSPENDED_EV,
  ConnectorStatus.SUSPENDED_EVSE,
]);

/**
 * Connector states where the backend RemoteTransactionGuard accepts a start.
 */
const STARTABLE_CONNECTOR_STATUSES: ReadonlySet<ConnectorStatus> = new Set([
  ConnectorStatus.AVAILABLE,
  ConnectorStatus.PREPARING,
]);

export const isConnectorBusy = (status: ConnectorStatus): boolean =>
  BUSY_CONNECTOR_STATUSES.has(status);

export const isConnectorStartable = (status: ConnectorStatus): boolean =>
  STARTABLE_CONNECTOR_STATUSES.has(status);

/**
 * Get display label for session status
 */
export const getSessionStatusLabel = (status: SessionStatus): string => {
  switch (status) {
    case SessionStatus.CHARGING:
      return 'Charging';
    case SessionStatus.FINISHED:
      return 'Finished';
    case SessionStatus.PREPARE:
      return 'Preparing';
    case SessionStatus.PENDING:
      return 'Pending';
    case SessionStatus.CONFIRMED:
      return 'Confirmed';
    case SessionStatus.CANCELLED:
      return 'Cancelled';
    case SessionStatus.UNAUTHORIZED:
      return 'Unauthorized';
    default:
      return String(status);
  }
};

/**
 * Get display label for transaction status
 */
export const getTransactionStatusLabel = (status: TransactionStatus): string => {
  switch (status) {
    case TransactionStatus.SUCCESS:
      return 'Success';
    case TransactionStatus.CREATED:
      return 'Created';
    case TransactionStatus.FAILURE:
      return 'Failed';
    case TransactionStatus.PROCESSING:
      return 'Processing';
    case TransactionStatus.REVERSED:
      return 'Reversed';
    case TransactionStatus.PENDING:
      return 'Pending';
    default:
      return String(status);
  }
};
